import React, { useState } from 'react';
import styles from './RussianCardPayment.module.css';
import { EXECUTOR_DETAILS } from '../../config/robokassa';
import { createYooKassaPayment } from '../../utils/yookassa';
import { YOOKASSA_CONFIG } from '../../config/yookassa';

const RussianCardPayment = ({ onBack }) => {
  const [paymentMethod, setPaymentMethod] = useState(null); // null, 'robokassa', 'yookassa'
  const [showIframe, setShowIframe] = useState(false);
  const [isLoadingYooKassa, setIsLoadingYooKassa] = useState(false);
  const [yooKassaError, setYooKassaError] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState(null);
  const [showPromoCodeInput, setShowPromoCodeInput] = useState(false);
  
  const BASE_PRICE = YOOKASSA_CONFIG.AMOUNT;
  const PROMO_CODE = 'IamSuper';
  const DISCOUNT_PERCENT = 30;
  
  // Вычисляем цену с учетом скидки
  const calculatePrice = () => {
    if (promoCodeApplied) {
      const discount = BASE_PRICE * (DISCOUNT_PERCENT / 100);
      return BASE_PRICE - discount;
    }
    return BASE_PRICE;
  };
  
  const finalPrice = calculatePrice();
  
  // Проверка промокода (регистронезависимая)
  const handlePromoCodeApply = () => {
    setPromoCodeError(null);
    const trimmedCode = promoCode.trim();
    
    if (!trimmedCode) {
      setPromoCodeError('Введите промокод');
      return;
    }
    
    // Сравниваем без учета регистра
    if (trimmedCode.toLowerCase() === PROMO_CODE.toLowerCase()) {
      setPromoCodeApplied(true);
      setPromoCodeError(null);
    } else {
      setPromoCodeError('Промокод неверный');
      setPromoCodeApplied(false);
    }
  };
  
  // Сброс промокода
  const handlePromoCodeRemove = () => {
    setPromoCode('');
    setPromoCodeApplied(false);
    setPromoCodeError(null);
  };
  
  const ROBOKASSA_INVOICE_URL = 'https://auth.robokassa.ru/merchant/Invoice/addLEN41JkKtud5jT_-1zw';

  const handleRobokassaPayment = () => {
    setPaymentMethod('robokassa');
    setShowIframe(true);
  };

  const handleYooKassaPayment = async () => {
    setYooKassaError(null);
    setIsLoadingYooKassa(true);

    try {
      // Создаем платеж через backend API с учетом промокода
      const paymentAmount = finalPrice;
      const appliedPromoCode = promoCodeApplied ? PROMO_CODE : null;
      
      await createYooKassaPayment(
        paymentAmount,
        YOOKASSA_CONFIG.DESCRIPTION,
        YOOKASSA_CONFIG.RETURN_URL,
        appliedPromoCode
      );
    } catch (error) {
      console.error('Ошибка при создании платежа ЮKassa:', error);
      setYooKassaError(error.message || 'Не удалось создать платеж. Попробуйте позже.');
      setIsLoadingYooKassa(false);
    }
  };

  const handleBackToPayment = () => {
    setPaymentMethod(null);
    setShowIframe(false);
    setYooKassaError(null);
    setIsLoadingYooKassa(false);
  };

  // Если показываем форму оплаты Робокассы
  if (paymentMethod === 'robokassa' && showIframe) {
    return (
      <div className={styles.container}>
        <button 
          className={styles.backButton} 
          onClick={handleBackToPayment}
          type="button"
          aria-label="Вернуться к выбору платежной системы"
        >
          ← Назад
        </button>
        
        <div className={styles.paymentInfo}>
          <div className={styles.clockIcon}>
            <div className={styles.clockFace}>
              <div className={styles.hourHand}></div>
              <div className={styles.minuteHand}></div>
            </div>
          </div>
          <p className={styles.paymentInfoText}>
            Оплата может занять время — зависит от скорости. Немного терпения.
            <br />
            После успешной оплаты дождитесь когда вас переведет на анкету или нажмите на кнопку "вернуться в магазин".
          </p>
        </div>
        
        <div className={styles.iframeContainer}>
          <iframe
            src={ROBOKASSA_INVOICE_URL}
            className={styles.paymentIframe}
            title="Оплата через Робокассу"
            allow="payment"
            sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      </div>
    );
  }

  // Показываем выбор платежной системы
  return (
    <div className={styles.container}>
      {onBack && (
        <button 
          className={styles.backButton} 
          onClick={onBack}
          type="button"
          aria-label="Вернуться к выбору способа оплаты"
        >
          ← Назад
        </button>
      )}
      
      <div className={styles.priceSection}>
        <div className={styles.priceContainer}>
          {promoCodeApplied && (
            <div className={styles.originalPrice}>{BASE_PRICE.toLocaleString('ru-RU')} ₽</div>
          )}
          <div className={styles.price}>
            {finalPrice.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
          </div>
          {promoCodeApplied && (
            <div className={styles.discountBadge}>Скидка {DISCOUNT_PERCENT}%</div>
          )}
        </div>
        
        {/* Поле для промокода */}
        <div className={styles.promoCodeSection}>
          {!promoCodeApplied && showPromoCodeInput && (
            <div className={styles.promoCodeInputGroup}>
              <input
                type="text"
                className={styles.promoCodeInput}
                placeholder="Введите промокод"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  setPromoCodeError(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePromoCodeApply();
                  }
                }}
                autoFocus
              />
              <button
                className={styles.promoCodeButton}
                onClick={handlePromoCodeApply}
                type="button"
              >
                Применить
              </button>
            </div>
          )}
          {promoCodeApplied && (
            <div className={styles.promoCodeApplied}>
              <span className={styles.promoCodeSuccess}>
                ✓ Промокод "{PROMO_CODE}" применен
              </span>
              <button
                className={styles.promoCodeRemove}
                onClick={handlePromoCodeRemove}
                type="button"
              >
                Удалить
              </button>
            </div>
          )}
          {promoCodeError && (
            <div className={styles.promoCodeError}>{promoCodeError}</div>
          )}
        </div>
        
        {yooKassaError && (
          <div className={styles.errorContainer}>
            <p className={styles.errorText}>{yooKassaError}</p>
          </div>
        )}
        
        <div className={styles.paymentButtonsGroup}>
          <button
            className={styles.paymentButton}
            onClick={handleYooKassaPayment}
            type="button"
            disabled={isLoadingYooKassa}
          >
            {isLoadingYooKassa ? 'Загрузка...' : 'Оплатить через ЮKassa'}
          </button>
          {!promoCodeApplied && !showPromoCodeInput && (
            <button
              className={styles.promoCodeLink}
              onClick={() => setShowPromoCodeInput(true)}
              type="button"
            >
              У меня есть промокод
            </button>
          )}
        </div>
        
        <div className={styles.executorDetails}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Ф.И.О.:</span>
            <span className={styles.detailValue}>{EXECUTOR_DETAILS.FULL_NAME}</span>
          </div>
          
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>ИНН:</span>
            <span className={styles.detailValue}>{EXECUTOR_DETAILS.INN}</span>
          </div>
          
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Правовой статус:</span>
            <span className={styles.detailValue}>{EXECUTOR_DETAILS.LEGAL_STATUS}</span>
          </div>
          
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>E-mail:</span>
            <span className={styles.detailValue}>{EXECUTOR_DETAILS.EMAIL}</span>
          </div>
          
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Сайт:</span>
            <span className={styles.detailValue}>{EXECUTOR_DETAILS.WEBSITE}</span>
          </div>
          
          <div className={styles.offerLink}>
            <a 
              href={EXECUTOR_DETAILS.OFFER_URL} 
              target="_blank"
              rel="noopener noreferrer"
            >
              Публичная оферта
            </a>
          </div>
        </div>
        
        <button
          className={styles.paymentButton}
          onClick={handleRobokassaPayment}
          type="button"
        >
          Альтернативный способ оплаты
        </button>
      </div>
    </div>
  );
};

export default RussianCardPayment;
